import _ from "lodash"
import * as React from "react"

import { useEditorContext } from "../components/EditorContext"

interface UseStatefulFieldParams<Value, Error> {
  initialValue: Value
  // Calling dangerouslyUpdateValue triggers an immediate update of the value, followed by a new call to onChange.
  // Make sure that the new value will not trigger a new call to dangerouslyUpdateValue to avoid an infinite loop.
  onChange: (newValue: Value, dangerouslyUpdateValue: (value: Value) => void) => void
  editorError?: Error | undefined
  validate?: (newValue: Value) => Error | undefined // Takes precedence over editorError, if provided.
  debounce?: boolean
}

interface UseStatefulFieldResult<Value, Error> {
  value: Value
  error: Error | undefined
  handleChange(value: Value): void
  handleBlur(): void
}

const useStatefulField = <Value, Error = string>({
  initialValue,
  onChange,
  validate,
  editorError,
  debounce = true,
}: UseStatefulFieldParams<Value, Error>): UseStatefulFieldResult<Value, Error> => {
  const [value, setValue] = React.useState<Value>(initialValue)
  const [validateError, setValidateError] = React.useState<Error | undefined>(validate?.(initialValue))

  // Re-render when the editor hash changes to display potential editor errors.
  useEditorContext()

  const changeAndValidate: typeof onChange = React.useCallback(
    (newValue, dangerouslyUpdateValue) => {
      onChange(newValue, dangerouslyUpdateValue)
      setValidateError(validate?.(newValue))
    },
    [onChange, validate],
  )

  const debouncedOnChange = React.useMemo(
    () =>
      _.debounce(changeAndValidate, 500, {
        maxWait: 500,
      }),
    [changeAndValidate],
  )

  const handleChange = (newValue: Value): void => {
    setValue(newValue)
    setValidateError(validate?.(newValue))
    if (debounce) {
      debouncedOnChange(newValue, handleChange)
    } else {
      changeAndValidate(newValue, handleChange)
    }
  }

  const handleBlur = (): void => debouncedOnChange.flush()

  return { value, error: validateError ?? editorError, handleChange, handleBlur }
}

export default useStatefulField
